import type { IndexResponse, FileResponse } from "./types.ts";

let redirecting = false;

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    // not logged in — bounce to the OIDC login and come back here (once)
    if (!redirecting) {
      redirecting = true;
      window.location.href = "/auth/login?returnTo=" + encodeURIComponent(location.pathname + location.search);
    }
    throw new Error("redirecting to login…");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error || `${res.status} ${res.statusText}`);
  return data as T;
}

export const api = {
  index: () => jfetch<IndexResponse>("/api/index"),

  // 不走 jfetch 的自動跳轉：無存取權回傳 canView=false（顯示落地頁），並帶 viewPin 供落地頁決定要不要顯示 PIN 欄。
  me: async (): Promise<{
    authEnabled: boolean;
    user: { name: string; email: string } | null;
    canView: boolean;
    canEdit: boolean;
    guest: boolean;
    viewPin: boolean;
    viewPinLen: number;
  }> => {
    const res = await fetch("/api/me");
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return {
      authEnabled: (data.authEnabled as boolean) ?? true,
      user: (data.user as { name: string; email: string } | null) ?? null,
      canView: (data.canView as boolean) ?? false,
      canEdit: (data.canEdit as boolean) ?? false,
      guest: (data.guest as boolean) ?? false,
      viewPin: (data.viewPin as boolean) ?? false,
      viewPinLen: (data.viewPinLen as number) ?? 0,
    };
  },

  config: () => jfetch<{ repo: string; branch: string; roots: string[] }>("/api/config"),

  file: (path: string) => jfetch<FileResponse>(`/api/file?path=${encodeURIComponent(path)}`),

  save: (body: { path: string; content: string; sha?: string; message?: string }) =>
    jfetch<{ ok: true; sha: string }>("/api/save", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  upload: (filename: string, dataBase64: string) =>
    jfetch<{ ok: true; path: string; url: string }>("/api/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename, dataBase64 }),
    }),

  remove: (body: { path: string; sha: string; message?: string }) =>
    jfetch<{ ok: true }>("/api/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  // 路徑（資料夾）結構操作：搬移/改名 與 連內容一起刪除。各自一個 commit。
  renamePath: (from: string, to: string) =>
    jfetch<{ ok: true; from: string; to: string; count: number }>("/api/rename-path", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ from, to }),
    }),

  deletePath: (dir: string) =>
    jfetch<{ ok: true; dir: string; count: number }>("/api/delete-path", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dir }),
    }),

  // PIN 訪客登入（唯讀）／離開
  guest: (pin: string) =>
    jfetch<{ ok: true; canView: boolean }>("/api/guest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin }),
    }),

  leave: () => jfetch<{ ok: true }>("/api/leave", { method: "POST" }),

  // 觸發 Deploy workflow（僅編輯者；訪客會被伺服器端擋下 403）
  deploy: () => jfetch<{ ok: true; repo: string; workflow: string }>("/api/deploy", { method: "POST" }),
};
